import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth, appLogout } from '@/hooks/useAppAuth';
import DaumPostcodeSearch from '@/components/base/DaumPostcodeSearch';
import { REGION_HIERARCHY, ALL_PROVINCES } from '@/mocks/regionData';
import {
  certificateOptions,
  dayOptions,
  timeOptions,
} from '@/mocks/caregivers';

type AddressValue = { zonecode: string; roadAddress: string; jibunAddress: string; detailAddress: string };

type CareerEntry = { workplace: string; startDate: string; endDate: string; role: string; description: string };
type CertImage = { url: string; name: string };

function parseAddressString(addr: string): AddressValue {
  if (!addr) return { zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' };
  const zoneMatch = addr.match(/^\((\d+)\)\s/);
  const zonecode = zoneMatch ? zoneMatch[1] : '';
  const rest = zoneMatch ? addr.slice(zoneMatch[0].length) : addr;
  const commaIdx = rest.lastIndexOf(', ');
  const roadAddress = commaIdx > 0 ? rest.slice(0, commaIdx) : rest;
  const detailAddress = commaIdx > 0 ? rest.slice(commaIdx + 2) : '';
  return { zonecode, roadAddress, jibunAddress: '', detailAddress };
}

function formatAddressString(av: AddressValue): string {
  return `(${av.zonecode}) ${av.roadAddress}${av.detailAddress ? ', ' + av.detailAddress : ''}`;
}

function displayAddressSimple(addr: string): string {
  if (!addr) return '미입력';
  const cleaned = addr.replace(/^\(\d+\)\s/, '');
  return cleaned || addr;
}

export default function AppProfileEditPage() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const auth = useAppAuth(true);
  const [profile, setProfile] = useState<{
    id: string;
    roles: ('patient' | 'caregiver')[];
    name: string;
    phone: string;
    gender: string;
    birth_date: string;
    address: string;
    avatar_url?: string;
  } | null>(null);
  const [cgProfile, setCgProfile] = useState({
    experience_years: 0,
    hourly_rate: 15000,
    available_regions: [] as string[],
    certificates: [] as string[],
    introduction: '',
    available_days: [] as string[],
    available_times: [] as string[],
    is_active: true,
    career_history: [] as CareerEntry[],
    certificate_images: [] as CertImage[],
  });
  const [addressValue, setAddressValue] = useState<AddressValue>({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // 지역 선택 아코디언
  const [expandedProvinces, setExpandedProvinces] = useState<string[]>([]);

  useEffect(() => {
    if (!auth.userId) return;
    setLoading(true);
    async function load() {
      const { data: p } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', auth.userId)
        .maybeSingle();
      if (!p) { navigateRef.current('/app/register'); return; }
      const profileData = { ...p, roles: (p.roles as ('patient' | 'caregiver')[]) || [] };
      setProfile(profileData);
      setAddressValue(parseAddressString(p.address || ''));

      if (profileData.roles.includes('caregiver')) {
        const { data: cg } = await supabase
          .from('caregiver_profiles')
          .select('*')
          .eq('user_id', auth.userId)
          .maybeSingle();
        if (cg) {
          setCgProfile({
            experience_years: cg.experience_years || 0,
            hourly_rate: cg.hourly_rate || 15000,
            available_regions: cg.available_regions || [],
            certificates: cg.certificates || [],
            introduction: cg.introduction || '',
            available_days: cg.available_days || [],
            available_times: cg.available_times || [],
            is_active: cg.is_active ?? true,
            career_history: cg.career_history || [],
            certificate_images: cg.certificate_images || [],
          });
        }
      }
      setLoading(false);
    }
    load();
  }, [auth.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleArray(arr: string[], val: string) {
    return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
  }

  function toggleProvinceExpand(province: string) {
    setExpandedProvinces((prev) =>
      prev.includes(province) ? prev.filter((p) => p !== province) : [...prev, province]
    );
  }

  function toggleDistrict(province: string, district: string) {
    const regionKey = `${province} ${district}`;
    setCgProfile({
      ...cgProfile,
      available_regions: toggleArray(cgProfile.available_regions, regionKey),
    });
  }

  function getProvinceSelectedCount(province: string): number {
    const districts = REGION_HIERARCHY[province] || [];
    return districts.filter((d) => cgProfile.available_regions.includes(`${province} ${d}`)).length;
  }

  function getProvinceTotalCount(province: string): number {
    return (REGION_HIERARCHY[province] || []).length;
  }

  // 자격증 이미지 업로드
  const [uploadingCert, setUploadingCert] = useState(false);

  async function handleCertImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    if (cgProfile.certificate_images.length >= 5) {
      alert('자격증 이미지는 최대 5개까지 등록 가능합니다.');
      return;
    }
    setUploadingCert(true);
    try {
      const fileName = `cert_${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('certificates')
        .upload(`${auth.userId}/${fileName}`, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('certificates')
        .getPublicUrl(`${auth.userId}/${fileName}`);

      setCgProfile({
        ...cgProfile,
        certificate_images: [...cgProfile.certificate_images, { url: urlData.publicUrl, name: file.name }],
      });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploadingCert(false);
    }
  }

  function removeCertImage(index: number) {
    setCgProfile({
      ...cgProfile,
      certificate_images: cgProfile.certificate_images.filter((_, i) => i !== index),
    });
  }

  // 경력 추가/삭제
  function addCareerEntry() {
    setCgProfile({
      ...cgProfile,
      career_history: [
        ...cgProfile.career_history,
        { workplace: '', startDate: '', endDate: '', role: '간병인', description: '' },
      ],
    });
  }

  function updateCareerEntry(index: number, field: keyof CareerEntry, value: string) {
    const updated = [...cgProfile.career_history];
    updated[index] = { ...updated[index], [field]: value };
    setCgProfile({ ...cgProfile, career_history: updated });
  }

  function removeCareerEntry(index: number) {
    setCgProfile({
      ...cgProfile,
      career_history: cgProfile.career_history.filter((_, i) => i !== index),
    });
  }

  async function handleSave() {
    if (!profile) return;
    setSaving(true);
    setMsg('');

    const { error: pErr } = await supabase
      .from('profiles')
      .update({
        name: profile.name,
        phone: profile.phone,
        gender: profile.gender,
        birth_date: profile.birth_date,
        address: formatAddressString(addressValue),
      })
      .eq('id', profile.id);

    if (pErr) {
      setSaving(false);
      setMsg('저장 중 오류: ' + pErr.message);
      return;
    }

    if (profile.roles.includes('caregiver')) {
      const { data: existing } = await supabase
        .from('caregiver_profiles')
        .select('id')
        .eq('user_id', profile.id)
        .maybeSingle();

      const payload = {
        experience_years: cgProfile.experience_years,
        hourly_rate: cgProfile.hourly_rate,
        available_regions: cgProfile.available_regions,
        certificates: cgProfile.certificates,
        introduction: cgProfile.introduction,
        available_days: cgProfile.available_days,
        available_times: cgProfile.available_times,
        is_active: cgProfile.is_active,
        career_history: cgProfile.career_history,
        certificate_images: cgProfile.certificate_images,
      };

      if (existing) {
        await supabase.from('caregiver_profiles').update(payload).eq('id', existing.id);
      } else {
        await supabase.from('caregiver_profiles').insert({ user_id: profile.id, ...payload });
      }
    }

    setSaving(false);
    setMsg('저장되었습니다!');
    setIsEditing(false);
    setTimeout(() => setMsg(''), 2000);
  }

  async function handleLogout() {
    await appLogout();
    navigate('/app');
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!profile) return null;

  const isCaregiver = profile.roles.includes('caregiver');
  const inputCls =
    'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white';

  // 읽기 모드용 헬퍼
  const genderLabel = profile.gender || '미입력';
  const birthLabel = profile.birth_date
    ? `${profile.birth_date.slice(0, 4)}.${profile.birth_date.slice(4, 6)}.${profile.birth_date.slice(6, 8)}`
    : '미입력';
  const phoneLabel = profile.phone
    ? `${profile.phone.slice(0, 3)}-${profile.phone.slice(3, 7)}-${profile.phone.slice(7, 11)}`
    : '미입력';

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">

        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">내 프로필</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {isCaregiver ? '간병인' : '환자/보호자'} 정보
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#2E7D6B] text-white text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
            >
              <i className="ri-edit-line" />
              수정하기
            </button>
          )}
        </div>

        {msg && (
          <div className="p-3 rounded-xl bg-green-50 text-xs text-green-600 text-center mb-4">
            {msg}
          </div>
        )}

        {/* ───── 읽기 모드 ───── */}
        {!isEditing && (
          <>
            {/* 기본 정보 카드 */}
            <div className="rounded-2xl border border-gray-100 p-5 mb-3 bg-white">
              <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-[#2E7D6B]" />
                기본 정보
              </h2>
              <div className="space-y-3">
                <InfoRow label="성명" value={profile.name || '미입력'} />
                <InfoRow label="휴대폰 번호" value={phoneLabel} />
                <div className="flex gap-6">
                  <div className="flex-1">
                    <InfoRow label="성별" value={genderLabel} />
                  </div>
                  <div className="flex-1">
                    <InfoRow label="생년월일" value={birthLabel} />
                  </div>
                </div>
                <InfoRow label="주소" value={displayAddressSimple(profile.address)} />
              </div>
            </div>

            {/* 간병인 정보 카드 */}
            {isCaregiver && (
              <div className="rounded-2xl border border-gray-100 p-5 mb-3 bg-white">
                <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-nurse-line text-[#1976D2]" />
                  간병인 상세 정보
                </h2>
                <div className="space-y-3">
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <InfoRow label="경력" value={`${cgProfile.experience_years}년`} />
                    </div>
                    <div className="flex-1">
                      <InfoRow label="희망 시급" value={`${cgProfile.hourly_rate.toLocaleString()}원`} />
                    </div>
                  </div>
                  <InfoRow
                    label="활동 가능 지역"
                    value={cgProfile.available_regions.length > 0 ? cgProfile.available_regions.join(', ') : '미선택'}
                  />
                  <InfoRow
                    label="보유 자격증"
                    value={cgProfile.certificates.length > 0 ? cgProfile.certificates.join(', ') : '미선택'}
                  />
                  <InfoRow
                    label="근무 가능 요일"
                    value={cgProfile.available_days.length > 0 ? cgProfile.available_days.join(', ') : '미선택'}
                  />
                  <InfoRow
                    label="근무 시간대"
                    value={cgProfile.available_times.length > 0 ? cgProfile.available_times.join(', ') : '미선택'}
                  />
                  <InfoRow
                    label="자기소개"
                    value={cgProfile.introduction || '미입력'}
                  />
                  <InfoRow
                    label="매칭 활성화"
                    value={cgProfile.is_active ? '활성' : '비활성'}
                  />

                  {/* 자격증 이미지 */}
                  {cgProfile.certificate_images.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-400 w-28 shrink-0 align-top pt-1 inline-block">자격증 이미지</span>
                      <div className="inline-flex flex-wrap gap-2">
                        {cgProfile.certificate_images.map((img, i) => (
                          <img key={i} src={img.url} alt={img.name} className="w-16 h-16 rounded-lg object-cover border border-gray-100" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 경력 상세 */}
                  {cgProfile.career_history.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-400 w-28 shrink-0 align-top pt-0.5 inline-block">경력 상세</span>
                      <div className="inline-flex flex-col gap-2">
                        {cgProfile.career_history.map((entry, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg px-3 py-2">
                            <p className="text-xs font-bold text-gray-800">
                              {entry.workplace}
                              <span className="text-gray-400 font-normal ml-1">{entry.role}</span>
                            </p>
                            {(entry.startDate || entry.endDate) && (
                              <p className="text-[10px] text-gray-400">
                                {entry.startDate || '미입력'} ~ {entry.endDate || '현재'}
                              </p>
                            )}
                            {entry.description && (
                              <p className="text-[11px] text-gray-600 mt-0.5">{entry.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ───── 편집 모드 ───── */}
        {isEditing && (
          <>
            {/* 기본 정보 */}
            <div className="rounded-2xl border border-gray-100 p-5 mb-3 bg-white">
              <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-[#2E7D6B]" />
                기본 정보
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">성명</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">휴대폰 번호</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    className={inputCls}
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">성별</label>
                    <select
                      value={profile.gender}
                      onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">선택</option>
                      <option value="남">남</option>
                      <option value="여">여</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">생년월일</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={profile.birth_date}
                      onChange={(e) => setProfile({ ...profile, birth_date: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                      placeholder="19900101"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">주소</label>
                  <DaumPostcodeSearch
                    value={addressValue}
                    onChange={setAddressValue}
                    placeholder="주소를 검색해주세요"
                  />
                </div>
              </div>
            </div>

            {/* 간병인 전용 상세 */}
            {isCaregiver && (
              <div className="rounded-2xl border border-gray-100 p-5 mb-3 bg-white space-y-5">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-nurse-line text-[#1976D2]" />
                  간병인 상세 정보
                </h2>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">경력 (년)</label>
                    <input
                      type="number"
                      value={cgProfile.experience_years}
                      onChange={(e) => setCgProfile({ ...cgProfile, experience_years: parseInt(e.target.value) || 0 })}
                      className={inputCls}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">희망 시급 (원)</label>
                    <input
                      type="number"
                      value={cgProfile.hourly_rate}
                      onChange={(e) => setCgProfile({ ...cgProfile, hourly_rate: parseInt(e.target.value) || 0 })}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">활동 가능 지역</label>
                  {cgProfile.available_regions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {cgProfile.available_regions.map((region) => (
                        <span key={region} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#f0f9f6] border border-[#b8e6d8] text-xs text-[#2E7D6B] font-medium">
                          {region}
                          <button
                            onClick={() => setCgProfile({ ...cgProfile, available_regions: cgProfile.available_regions.filter((r) => r !== region) })}
                            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-[#d4f0e5] cursor-pointer"
                          >
                            <i className="ri-close-line text-[10px]" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="space-y-2">
                    {ALL_PROVINCES.map((province) => {
                      const isExpanded = expandedProvinces.includes(province);
                      const selectedCount = getProvinceSelectedCount(province);
                      const totalCount = getProvinceTotalCount(province);
                      const districts = REGION_HIERARCHY[province] || [];

                      return (
                        <div key={province} className="rounded-xl border border-gray-100 overflow-hidden">
                          <button
                            onClick={() => toggleProvinceExpand(province)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors ${
                              isExpanded ? 'bg-[#f0f9f6]' : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold ${selectedCount > 0 ? 'text-[#2E7D6B]' : 'text-gray-600'}`}>
                                {province}
                              </span>
                              {selectedCount > 0 && (
                                <span className="px-1 py-0.5 rounded-md bg-[#2E7D6B] text-[10px] font-bold text-white">
                                  {selectedCount}/{totalCount}
                                </span>
                              )}
                            </div>
                            <i className={`ri-arrow-down-s-line text-gray-400 text-xs transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          {isExpanded && (
                            <div className="px-3 py-2.5 bg-gray-50/50 border-t border-gray-100">
                              <div className="flex flex-wrap gap-1.5">
                                {districts.map((district) => {
                                  const regionKey = `${province} ${district}`;
                                  const isSelected = cgProfile.available_regions.includes(regionKey);
                                  return (
                                    <button
                                      key={regionKey}
                                      onClick={() => toggleDistrict(province, district)}
                                      className={`px-2 py-1 rounded-lg border text-[11px] cursor-pointer transition-all whitespace-nowrap ${
                                        isSelected
                                          ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6] font-medium'
                                          : 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'
                                      }`}
                                    >
                                      {district}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">보유 자격증</label>
                  <div className="flex flex-wrap gap-2">
                    {certificateOptions.map((c) => (
                      <button
                        key={c}
                        onClick={() => setCgProfile({ ...cgProfile, certificates: toggleArray(cgProfile.certificates, c) })}
                        className={`px-3 py-2 rounded-xl text-xs font-medium border-2 cursor-pointer transition-all whitespace-nowrap ${
                          cgProfile.certificates.includes(c)
                            ? 'border-[#1976D2] bg-blue-50 text-[#1976D2]'
                            : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">근무 가능 요일</label>
                  <div className="flex gap-2">
                    {dayOptions.map((d) => (
                      <button
                        key={d}
                        onClick={() => setCgProfile({ ...cgProfile, available_days: toggleArray(cgProfile.available_days, d) })}
                        className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 cursor-pointer transition-all ${
                          cgProfile.available_days.includes(d)
                            ? 'border-[#2E7D6B] bg-[#f0f9f6] text-[#2E7D6B]'
                            : 'border-gray-100 bg-white text-gray-400 hover:border-gray-200'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">근무 가능 시간대</label>
                  <div className="flex flex-col gap-2">
                    {timeOptions.map((t) => (
                      <button
                        key={t}
                        onClick={() => setCgProfile({ ...cgProfile, available_times: toggleArray(cgProfile.available_times, t) })}
                        className={`w-full py-3 rounded-xl text-sm font-medium border-2 cursor-pointer transition-all flex items-center gap-3 px-4 ${
                          cgProfile.available_times.includes(t)
                            ? 'border-[#2E7D6B] bg-[#f0f9f6] text-[#2E7D6B]'
                            : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        {cgProfile.available_times.includes(t) ? (
                          <i className="ri-checkbox-circle-line" />
                        ) : (
                          <i className="ri-checkbox-blank-circle-line" />
                        )}
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">자기소개</label>
                  <textarea
                    value={cgProfile.introduction}
                    onChange={(e) => setCgProfile({ ...cgProfile, introduction: e.target.value })}
                    placeholder="경력, 특기사항, 간병 스타일 등을 자유롭게 작성해주세요."
                    rows={4}
                    maxLength={500}
                    className={`${inputCls} resize-none`}
                  />
                  <p className="text-[10px] text-gray-400 text-right mt-1">{cgProfile.introduction.length}/500</p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cgProfile.is_active}
                    onChange={(e) => setCgProfile({ ...cgProfile, is_active: e.target.checked })}
                    className="w-4 h-4 accent-[#2E7D6B] cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 font-medium">검색에 내 프로필 노출하기</span>
                </label>

                {/* 자격증 이미지 업로드 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">
                    <i className="ri-image-add-line mr-1 text-gray-400" />
                    자격증 이미지 (최대 5개)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {cgProfile.certificate_images.map((img, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                        <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeCertImage(i)}
                          className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white rounded-bl-lg flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-close-line text-[10px]" />
                        </button>
                      </div>
                    ))}
                    {cgProfile.certificate_images.length < 5 && (
                      <label className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-[#2E7D6B] hover:bg-[#f0f9f6] transition-colors">
                        {uploadingCert ? (
                          <div className="w-5 h-5 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <i className="ri-add-line text-gray-400 text-lg" />
                            <span className="text-[9px] text-gray-400 mt-0.5">추가</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCertImageUpload}
                          className="hidden"
                          disabled={uploadingCert}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400">자격증, 수료증 사진을 업로드해주세요. 신뢰도 향상에 도움이 됩니다.</p>
                </div>

                {/* 경력 상세 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-600">
                      <i className="ri-briefcase-line mr-1 text-gray-400" />
                      경력 상세
                    </label>
                    {cgProfile.career_history.length < 10 && (
                      <button
                        onClick={addCareerEntry}
                        className="text-[11px] font-medium text-[#2E7D6B] px-2 py-1 rounded-lg bg-[#f0f9f6] cursor-pointer hover:bg-[#d4f0e5] transition-colors whitespace-nowrap"
                      >
                        <i className="ri-add-line mr-0.5" />경력 추가
                      </button>
                    )}
                  </div>
                  {cgProfile.career_history.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center bg-gray-50 rounded-xl">
                      등록된 경력이 없습니다. 경력을 추가하면 신뢰도가 높아집니다.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {cgProfile.career_history.map((entry, i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100 relative">
                          <button
                            onClick={() => removeCareerEntry(i)}
                            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:border-red-200 transition-colors"
                          >
                            <i className="ri-close-line text-xs text-gray-400" />
                          </button>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">근무처</label>
                              <input
                                value={entry.workplace}
                                onChange={(e) => updateCareerEntry(i, 'workplace', e.target.value)}
                                placeholder="병원/요양원명"
                                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white outline-none focus:border-[#2E7D6B]"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">직책</label>
                              <input
                                value={entry.role}
                                onChange={(e) => updateCareerEntry(i, 'role', e.target.value)}
                                placeholder="간병인"
                                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white outline-none focus:border-[#2E7D6B]"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">시작일</label>
                              <input
                                type="date"
                                value={entry.startDate}
                                onChange={(e) => updateCareerEntry(i, 'startDate', e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white outline-none focus:border-[#2E7D6B]"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">종료일</label>
                              <input
                                type="date"
                                value={entry.endDate}
                                onChange={(e) => updateCareerEntry(i, 'endDate', e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white outline-none focus:border-[#2E7D6B]"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-500 mb-0.5">주요 업무</label>
                            <input
                              value={entry.description}
                              onChange={(e) => updateCareerEntry(i, 'description', e.target.value)}
                              placeholder="주요 간병 내용을 간략히 기재"
                              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white outline-none focus:border-[#2E7D6B]"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 저장 / 취소 버튼 */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 mb-3"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />저장 중...
                </span>
              ) : (
                '저장하기'
              )}
            </button>

            <button
              onClick={() => setIsEditing(false)}
              className="w-full py-3 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors mb-3"
            >
              취소
            </button>
          </>
        )}

        {/* 로그아웃 */}
        <button
          onClick={handleLogout}
          className="w-full py-3 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
        >
          로그아웃
        </button>
      </div>
    </AppLayout>
  );
}

/* 읽기 모드 정보 행 */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center">
      <span className="text-xs text-gray-400 w-28 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-medium break-all">{value}</span>
    </div>
  );
}